import React, {useRef} from 'react';

import {Button, Panel, useSmUp} from '@pexip/components';
import type {ParticipantID} from '@pexip/infinity';

import type {
    BreakoutParticipants,
    BreakoutRoomNames,
    BreakoutsEditPanelSave,
} from '../../types';
import {BreakoutRoomVariant} from '../../types';
import {PanelHeader} from '../PanelHeader/PanelHeader.view';
import {BreakoutRoom} from '../BreakoutRoom/BreakoutRoom.view';
import {TestId} from '../../../test/testIds';
import {BreakoutRoomsConfigurationMenu} from '../BreakoutRoomsConfiguration/BreakoutRoomsConfiguration.view';

import styles from './BreakoutEditPanel.module.scss';
import {useBreakoutEdit} from '../../hooks/useBreakoutEdit';

export const BreakoutEditPanel: React.FC<{
    currentParticipants: BreakoutParticipants;
    myIdentity?: ParticipantID;
    breakoutRoomsNames: BreakoutRoomNames;
    saveButtonText: string;
    cancelButtonText: string;
    headerText?: string;
    onSave: (data: BreakoutsEditPanelSave) => void;
    onCancel: () => void;
    onClosePanel: () => void;
    onSettingsButtonClick?: () => void;
}> = ({
    currentParticipants,
    myIdentity,
    breakoutRoomsNames: breakoutRoomsNamesProp,
    saveButtonText,
    cancelButtonText,
    headerText,
    onSave,
    onCancel,
    onClosePanel,
    onSettingsButtonClick,
}) => {
    const isSmUp = useSmUp();

    const scrollElementRef = useRef<HTMLElement>(null);

    const {
        participants,
        breakoutRoomsNames,
        isDragging,
        isShuffling,
        onAddRoom,
        doSave,
        doShuffle,
        currentDragTarget,
        handleBreakoutRoomNameChange,
        onParticipantDragging,
        onParticipantDrag,
        changeParticipantRoom,
        onRemoveRoom,
        getBreakoutRoomRefCallback,
    } = useBreakoutEdit({
        currentParticipants,
        breakoutRoomsNames: breakoutRoomsNamesProp,
        myIdentity,
        onSave,
    });

    return (
        <Panel
            scrollElementRef={scrollElementRef}
            overflowHidden={isDragging}
            isRounded={isSmUp}
            padding="none"
            footerPadding="small"
            headerContent={
                <PanelHeader
                    title={headerText}
                    onCloseClick={onClosePanel}
                    closeButtonTestId={TestId.PanelHeaderCloseButton}
                    endContent={
                        <BreakoutRoomsConfigurationMenu
                            onAddRoomButtonClick={onAddRoom}
                            onSettingsButtonClick={onSettingsButtonClick}
                            onShuffledButtonClick={doShuffle}
                            isShuffling={isShuffling}
                        />
                    }
                />
            }
            footerContent={
                <div className={styles.footer}>
                    <Button
                        variant="secondary"
                        modifier="fullWidth"
                        onClick={onCancel}
                    >
                        {cancelButtonText}
                    </Button>
                    <Button
                        modifier="fullWidth"
                        onClick={doSave}
                        data-testid={TestId.ButtonSaveBreakoutRoomsEdit}
                    >
                        {saveButtonText}
                    </Button>
                </div>
            }
            scrollElementClassname="pt-2 pr-3 pb-3 pl-3"
        >
            {Array.from(participants.entries()).map(
                ([roomId, roomParticipants]) => {
                    return (
                        <BreakoutRoom
                            scrollElementRef={scrollElementRef}
                            variant={BreakoutRoomVariant.Edit}
                            id={roomId}
                            key={roomId}
                            name={breakoutRoomsNames.get(roomId) ?? roomId}
                            rooms={Array.from(participants.keys()).reduce(
                                (acc, current) => {
                                    if (roomId !== current) {
                                        acc[current] =
                                            breakoutRoomsNames.get(current) ??
                                            current;
                                    }
                                    return acc;
                                },
                                {} as Record<string, string>,
                            )}
                            myIdentity={myIdentity}
                            participants={roomParticipants}
                            isDragTarget={currentDragTarget === roomId}
                            onNameChanged={handleBreakoutRoomNameChange}
                            onParticipantDragging={onParticipantDragging}
                            onParticipantDrag={onParticipantDrag}
                            onParticipantRoomChange={changeParticipantRoom}
                            onRemoveRoom={onRemoveRoom}
                            ref={getBreakoutRoomRefCallback(roomId)}
                            canEditRoomName={!breakoutRoomsNames.get(roomId)}
                        />
                    );
                },
            )}
        </Panel>
    );
};

export type BreakoutEditPanelProps = React.ComponentProps<
    typeof BreakoutEditPanel
>;
